// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Renders an area only when the verified session was offered its capability.
 *
 * This is a usability affordance, explicitly permitted by
 * `07-SECURITY.md` §15.4 ("the UI may improve usability by hiding unavailable
 * actions, but the backend must enforce authorization independently") and
 * bounded by `CST-048` and `ADM-014` (client-side-only authorization is a
 * prohibited practice).
 *
 * Concretely: rendering a child here does not grant anything, and refusing to
 * render one does not protect anything. Every request the child makes is
 * authorised again by the BFF and by the service behind it.
 */

import type { ReactNode } from 'react';

import { useSession } from '../authentication/SessionProvider';
import { PermissionDeniedState } from '../shared-components/StateViews';
import { hasEveryCapability } from './capabilities';

/** Props for {@link RequireCapability}. */
export interface RequireCapabilityProps {
  /** Capabilities that must all have been offered to the session. */
  readonly required: readonly string[];
  /** Human-readable area name used in the denied message. */
  readonly area: string;
  /** Content rendered when the capabilities were offered. */
  readonly children: ReactNode;
}

/** Gate an administration area behind offered capabilities. */
export function RequireCapability({ required, area, children }: RequireCapabilityProps): ReactNode {
  const { session } = useSession();
  if (!hasEveryCapability(session, required)) {
    return <PermissionDeniedState area={area} />;
  }
  return <>{children}</>;
}
